import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ChartTooltipProps {
  top: number;
  left: number;
  children: ReactNode;
}

const ChartTooltip = ({ top, left, children }: ChartTooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      const { height, width } = tooltipRef.current.getBoundingClientRect();

      setPosition({ top: top - height, left: left - width / 2 });
    }
  }, [top, left]);

  return createPortal(
    <div
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
      }}
      role="tooltip"
      ref={tooltipRef}
    >
      {children}
    </div>,
    document.body
  );
};

const useChartTooltip = <T,>() => {
  const [tooltipState, setTooltipState] = useState<{
    isVisible: boolean;
    position: { x: number; y: number };
    data: T;
  } | null>(null);

  const showTooltip = (position: { x: number; y: number }, data: T) => {
    setTooltipState({ isVisible: true, position, data });
  };
  const hideTooltip = () => {
    setTooltipState(null);
  };

  return { tooltipState, showTooltip, hideTooltip };
};

export { ChartTooltip, useChartTooltip };
